import React, { useState } from "react";
import settings from "@src/settings";
import Hero from "@src/components/Hero";
import Layout from "@src/components/Layout";
import {
	Section,
	SectionIntro,
	Lead,
	Container,
	Header,
} from "@src/components/IndexPage/styles";
import Float from "@src/components/Float";
import SectionTitle from "@src/components/SectionTitle";
import Intro from "@src/components/Intro";
import Footer from "@src/components/Footer";
import JobInfo from "@src/components/JobInfo";
import VoiceDetail from "@src/components/VoiceDetail";
import VoiceItem from "@src/components/VoiceItem";
import GalleryIntro from "./GalleryIntro";
import Modal from "@src/components/Modal";
import Gallery from "@src/components/Gallery";
import { GatsbyImageProps } from "gatsby-plugin-image";
import { useWindowSize } from "@src/utils/useWindowSize";

type ImageComponent =
	| React.ReactElement<HTMLImageElement>
	| React.FunctionComponent<GatsbyImageProps>;
interface SectionTitles {
	title: string;
	subtitle: string;
}

export interface Props {
	showGrid?: boolean;
	animateIn?: boolean;
	contents: {
		header: {
			navInfo: {
				label: string;
				href: string;
			}[];
		};
		hero: {
			images: ImageComponent[];
		};
		intro: {
			sectionTitles: SectionTitles;
			info: {
				heading: string;
				caption: string;
				img: ImageComponent;
			}[];
		};
		voice: {
			sectionTitles: SectionTitles;
			lead: string;
			voices: {
				voiceInfo: {
					voiceNumber: number;
					nextVoice: number;
					voiceType: "インタビュー" | "対談";
					lead: string;
					staffInfo: {
						position: string;
						name: string;
						ruby: string;
					}[];
					listImg: ImageComponent;
					img: ImageComponent;
				};
				article: {
					type: "full" | "column";
					reverse: boolean;
					img: ImageComponent;
					content: {
						type: "Q" | "A" | "speaker" | "host" | "continued";
						name?: string;
						text: string;
					}[];
				}[];
				direction: "fromRight" | "fromLeft";
			}[];
		};
		gallery: {
			sectionTitles: SectionTitles;
			lead: string;
			introImg: ImageComponent;
			images: {
				caption?: string;
				img: ImageComponent;
			}[];
		};
		jobInfo: {
			sectionTitles: SectionTitles;
			info: {
				title: string;
				data: {
					title: string;
					detail: string;
				}[];
			}[];
		};
		contact: {
			lead: string;
			tel: string;
			note: string;
		};
	};
}

const IndexPage: React.FC<Props> = (props) => {
	const { contents, showGrid, animateIn = true } = props;
	const windowSize = useWindowSize();
	const [activeVoice, setActiveVoice] = useState(0);
	const [voiceVisible, setVoiceVisible] = useState(false);
	const [galleryVisible, setGalleryVisible] = useState(false);
	const updateVoice = (num: number) => setActiveVoice(num);
	const showVoice = (num: number) => {
		setActiveVoice(num), setVoiceVisible(true);
	};
	const showGallery = () => {
		setGalleryVisible(true);
	};
	const closeVoice = () => setVoiceVisible(false);
	const closeGallery = () => {
		console.log("close");
		setGalleryVisible(false);
	};
	return (
		<Layout showGrid={showGrid} animateIn={animateIn}>
			<Header navInfo={contents.header.navInfo} />
			<Hero images={contents.hero.images} />
			<Section backgroundColor={settings.color.pale} id="companyIntro">
				<Float animateIn={animateIn}>
					<SectionTitle {...contents.intro.sectionTitles} />
					<Intro info={contents.intro.info} windowW={windowSize.width} />
				</Float>
			</Section>
			<Section id="voice">
				<Container windowW={windowSize.width}>
					<Float animateIn={animateIn}>
						<SectionIntro>
							<SectionTitle {...contents.voice.sectionTitles} />
							<Lead>{contents.voice.lead}</Lead>
						</SectionIntro>
					</Float>
					{contents.voice.voices.map((voice, index) => (
						<Float key={`voiceItem${index}`}>
							<VoiceItem
								onClick={() => showVoice(index)}
								direction={index % 2 != 0 ? "fromLeft" : "fromRight"}
								{...{ ...voice.voiceInfo, img: voice.voiceInfo.listImg }}
							/>
						</Float>
					))}
				</Container>
			</Section>
			<Section id="gallery">
				<Float animateIn={animateIn}>
					<GalleryIntro
						showGallery={showGallery}
						sectionTitles={contents.gallery.sectionTitles}
						lead={contents.gallery.lead}
						introImg={contents.gallery.introImg}
						windowW={windowSize.width}
					/>
				</Float>
			</Section>
			<Section id="jobInfo" backgroundColor={settings.color.pale}>
				<Float animateIn={animateIn}>
					<Container windowW={windowSize.width}>
						<SectionTitle {...contents.jobInfo.sectionTitles} />
						{contents.jobInfo.info.map((val, index) => (
							<JobInfo title={val.title} data={val.data} key={`job${index}`} />
						))}
					</Container>
				</Float>
			</Section>
			<Footer
				id="contact"
				lead={contents.contact.lead}
				tel={contents.contact.tel}
				note={contents.contact.note}
				windowW={windowSize.width}
			/>
			<Modal
				activeChild={activeVoice}
				isShown={voiceVisible}
				closeSelf={closeVoice}
				forVoice
			>
				{contents.voice.voices.map((val, index) => {
					const nextVoice = contents.voice.voices.find(
						(thisVal) =>
							thisVal.voiceInfo.voiceNumber == val.voiceInfo.nextVoice
					);
					return (
						<VoiceDetail
							key={`voice${index}`}
							voiceInfo={val.voiceInfo}
							article={val.article}
							nextVoiceInfo={
								nextVoice && {
									...nextVoice.voiceInfo,
									updateFunc: () =>
										updateVoice(
											contents.voice.voices.findIndex(
												(val) =>
													val.voiceInfo.voiceNumber ==
													nextVoice.voiceInfo.voiceNumber
											)
										),
								}
							}
						/>
					);
				})}
			</Modal>
			<Modal isShown={galleryVisible} closeSelf={closeGallery}>
				<Gallery images={contents.gallery.images} />
			</Modal>
		</Layout>
	);
};

export default IndexPage;
